import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CurrencyContext = createContext();

// Persist currency in BOTH localStorage and a cookie. On the live site, "www." and the bare
// apex domain are different origins, so localStorage set on one is invisible on the other —
// this was the root cause of a manually-selected currency (e.g. GBP) reverting to USD after a
// redirect/hard refresh. A cookie scoped to the root domain (".christianfamilyrecovery.com")
// is shared across both, so the choice survives. Falls back to a normal host-only cookie
// everywhere else (e.g. preview URLs) where widening the domain isn't safe or useful.
const getCookieDomain = () => {
  const host = window.location.hostname;
  return host.includes('christianfamilyrecovery.com') ? '.christianfamilyrecovery.com' : null;
};

const setCurrencyCookie = (value) => {
  try {
    const domain = getCookieDomain();
    document.cookie = `selected-currency=${value}; path=/; max-age=31536000; SameSite=Lax${domain ? `; domain=${domain}` : ''}`;
  } catch {}
};

const getCurrencyCookie = () => {
  try {
    const match = document.cookie.match(/(?:^|; )selected-currency=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('USD');
  const [symbol, setSymbol] = useState('$');
  const [rates, setRates] = useState({});
  const [country, setCountry] = useState('US');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    detectLocationAndCurrency();
  }, []);

  const detectLocationAndCurrency = async () => {
    try {
      // Check if user has manually selected currency (localStorage first, cookie as fallback —
      // the cookie survives a www <-> apex domain redirect that would wipe localStorage)
      const savedCurrency = localStorage.getItem('selected-currency') || getCurrencyCookie();

      if (savedCurrency) {
        await loadCurrency(savedCurrency);
        try { localStorage.setItem('selected-currency', savedCurrency); } catch {}
        setCurrencyCookie(savedCurrency);
      } else {
        // Auto-detect based on IP
        const geoResponse = await axios.get(`${BACKEND_URL}/api/geo/detect`);
        const location = geoResponse.data.location;

        setCountry(location.country_code);
        await loadCurrency(location.currency);
        // Persist the detected currency so a later reload (or a flaky/slow geo lookup)
        // doesn't flip the displayed currency back and forth for the same visitor —
        // this was the main cause of currency detection looking "intermittent".
        try {
          localStorage.setItem('selected-currency', location.currency);
        } catch {}
        setCurrencyCookie(location.currency);
      }
    } catch (error) {
      console.error('Currency detection failed:', error);
      // Default to USD
      await loadCurrency('USD');
    } finally {
      setLoading(false);
    }
  };

  const loadCurrency = async (currencyCode) => {
    try {
      // Get prices in the currency
      const pricesResponse = await axios.get(
        `${BACKEND_URL}/api/currency/prices?currency=${currencyCode}`
      );
      
      setCurrency(currencyCode);
      setSymbol(pricesResponse.data.symbol);
      
      // Get exchange rates
      const ratesResponse = await axios.get(`${BACKEND_URL}/api/currency/rates`);
      setRates(ratesResponse.data.rates);
    } catch (error) {
      console.error('Failed to load currency:', error);
    }
  };

  const changeCurrency = async (newCurrency) => {
    localStorage.setItem('selected-currency', newCurrency);
    setCurrencyCookie(newCurrency);
    await loadCurrency(newCurrency);
  };

  const convertPrice = (usdPrice) => {
    if (currency === 'USD') {
      // USD is the base price — show it exactly as stored (e.g. $69.95 stays $69.95)
      const decimals = usdPrice % 1 === 0 ? 0 : 2;
      return usdPrice.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    }
    const rate = rates[currency] || 1;
    const converted = usdPrice * rate;
    // Never show decimals in converted currencies — round UP to the next whole unit.
    // Free ($0) stays free; anything else never displays below 5 units (a strong
    // currency shouldn't make our $5 entry price look like it converts to just 3 or 4).
    const roundedUp = usdPrice === 0 ? 0 : Math.max(Math.ceil(converted), 5);
    return roundedUp.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const formatPrice = (usdPrice, interval = null) => {
    const converted = convertPrice(usdPrice);
    const formatted = `${symbol}${converted}`;
    return interval ? `${formatted}/${interval}` : formatted;
  };

  // Special .99 rounding — used ONLY for the Everything plan's own price. GBP is a fixed
  // override (£8.99); every other non-USD currency rounds UP to the next value ending
  // in .99 (never a clean whole number, never down).
  const convertPrice99 = (usdPrice) => {
    if (currency === 'USD') {
      return usdPrice.toFixed(2);
    }
    if (currency === 'GBP' && usdPrice === 9.99) {
      return '8.99';
    }
    const rate = rates[currency] || 1;
    const converted = usdPrice * rate;
    const whole = Math.max(Math.ceil(converted - 0.99), 0);
    return (whole + 0.99).toFixed(2);
  };

  const formatPrice99 = (usdPrice, interval = null) => {
    const converted = convertPrice99(usdPrice);
    const formatted = `${symbol}${converted}`;
    return interval ? `${formatted}/${interval}` : formatted;
  };

  const value = {
    currency,
    symbol,
    country,
    rates,
    loading,
    changeCurrency,
    convertPrice,
    formatPrice,
    convertPrice99,
    formatPrice99
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
